// TruncationNotice — says out loud when a page is rendering only part of a table.
//
// Pair it with any /api/ops list response. It renders nothing when the response
// proves it holds every matching row, and a loud banner otherwise — including
// when the server could not establish the row total at all. Silence from this
// component means "provably complete", never "nothing came back".

import Banner from "./Banner";
import {
  rowWindowState,
  rowWindowMessage,
  rowWindowGroup,
  type RowWindowFields,
  type RowWindowSource,
} from "../../lib/rowWindow";

interface TruncationNoticeProps {
  /** The raw ops response (undefined while the first fetch is in flight). */
  response: RowWindowFields | null | undefined;
  /** Human name for the data, e.g. "EMS stock". */
  label: string;
  /** Optional extra sentence, e.g. what to do about it. */
  hint?: string;
  className?: string;
}

export default function TruncationNotice({
  response,
  label,
  hint,
  className,
}: TruncationNoticeProps) {
  const state = rowWindowState(response);
  const message = rowWindowMessage(state, label);
  if (message === null) return null;

  // Truncated is a known defect on screen right now → error. An unverifiable
  // total is an alarm, not a defect → warn. Neither is dismissible: a reader
  // must not be able to clear the notice while the numbers stay wrong.
  const truncated = state.kind === "truncated";
  return (
    <Banner
      variant={truncated ? "error" : "warn"}
      title={
        truncated
          ? `Incomplete data — ${label} is truncated`
          : `Unverified data — ${label} row total unknown`
      }
      className={className}
    >
      {message}
      {hint ? ` ${hint}` : ""}
    </Banner>
  );
}

interface TruncationNoticeGroupProps {
  sources: RowWindowSource[];
  className?: string;
}

/**
 * One notice for a page that reads several ops tables.
 *
 * Renders nothing only when every source that has answered proves it is whole.
 * A page with seven separate banners trains people to scroll past them, and a
 * warning that gets scrolled past is not a warning.
 */
export function TruncationNoticeGroup({
  sources,
  className,
}: TruncationNoticeGroupProps) {
  const group = rowWindowGroup(sources);
  if (group.severity === "none") return null;

  const truncated = group.severity === "truncated";
  return (
    <Banner
      variant={truncated ? "error" : "warn"}
      title={
        truncated
          ? `Incomplete data — ${group.lines.length} of ${group.checked} tables on this page are not fully loaded`
          : `Unverified data — ${group.lines.length} of ${group.checked} tables cannot confirm they are complete`
      }
      className={className}
    >
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {group.lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <p className="mt-1.5">
        Counts and charts on this page are computed over the rows that loaded.
        Treat them as a floor, not a total.
      </p>
    </Banner>
  );
}
